/* eslint-disable internal-rules/composable-consistent-naming */
import { RoadmapQueueNotClearedError } from '@/roadmaps/errors/roadmaps-errors';
import { reactive, ref, unref } from 'vue';
import pick from 'lodash/pick';
import isMatch from 'lodash/isMatch';
import some from 'lodash/some';
import { ROADMAP_REQUEST_FIELDS } from '../../../../roadmaps/constants/roadmaps.constants';
import { Queue } from './queue';
import store from '@/vuex/store';

const UPDATE_TYPES = {
    items: {
        key: 'items',
        propertiesToMatch: ROADMAP_REQUEST_FIELDS.item,
        getter: 'roadmaps/items/itemById',
    },
    swimlanes: {
        key: 'swimlanes',
        propertiesToMatch: ROADMAP_REQUEST_FIELDS.swimlane,
        getter: 'roadmaps/swimlanes/swimlaneById',
    },
    roadmaps: {
        key: 'roadmaps',
        propertiesToMatch: ROADMAP_REQUEST_FIELDS.roadmap,
        getter: 'roadmaps/roadmaps/roadmapById',
    },
    columns: {
        key: 'columns',
        propertiesToMatch: ROADMAP_REQUEST_FIELDS.column,
        getter: 'roadmaps/columns/columnById',
    },
    noUpdate: {
        key: 'noUpdate',
        propertiesToMatch: [],
        getter: '',
    },
};

class AsyncQueue extends Queue {
    constructor () {
        super();
        this._pendingTask = ref(false);
        this._queuedUpdates = reactive({
            [UPDATE_TYPES.items.key]: {},
            [UPDATE_TYPES.swimlanes.key]: {},
            [UPDATE_TYPES.roadmaps.key]: {},
            [UPDATE_TYPES.columns.key]: {},
            [UPDATE_TYPES.noUpdate.key]: {},
        });
    }

    get queuedUpdates () {
        return this._queuedUpdates;
    }

    get pendingTask () {
        return unref(this._pendingTask);
    }

    enqueue ({ task, updates, updateType }) {
        this._queuedUpdates[updateType] = {
            ...this._queuedUpdates[updateType],
            ...updates,
        };

        return new Promise((resolve, reject) => {
            super.enqueue({ task, updateType, resolve, reject });
            this.dequeue();
        });
    }

    async dequeue () {
        if (unref(this._pendingTask)) return;

        const queueItem = super.dequeue();

        try {
            this._pendingTask.value = true;

            const payload = await queueItem.task(this);

            this._pendingTask.value = false;

            if (super.size() > 0) {
                queueItem.resolve(payload);
                this.dequeue();
            } else {
                this.checkForError(queueItem.updateType);
                this.clear();
                queueItem.resolve(payload);
            }
        } catch (error) {
            this.clear();

            this._pendingTask.value = false;
            queueItem.reject(error);
        }
    }

    clear () {
        this._queuedUpdates.items = {};
        this._queuedUpdates.swimlanes = {};
        this._queuedUpdates.roadmaps = {};
        this._queuedUpdates.columns = {};
        this._queuedUpdates.noUpdate = {};
        const item = super.dequeue();
        if (!item) return false;

        item.resolve();
        this.clear();
    }

    checkForError (updateType) {
        const { propertiesToMatch, getter } = UPDATE_TYPES[updateType];

        const anyItemsDoNotMatch = some(this._queuedUpdates[updateType], (queuedUpdate) => {
            const storeItem = store.getters[getter](queuedUpdate.id);

            return !isMatch(pick(storeItem, propertiesToMatch), pick(queuedUpdate, propertiesToMatch));
        });

        if (anyItemsDoNotMatch) {
            throw new RoadmapQueueNotClearedError();
        }
    }
}

export const asyncQueue = new AsyncQueue();
